import React from "react";
import { Box, Tooltip, useTheme } from "@mui/material";
import { getPlatformColor, getPlatformShortLabel, surfaceTokens } from "../../config/theme";

export interface PlatformSplitSegment {
  provider: string;
  /** Exposure on THIS platform, 0–100, against that platform's own denominator. */
  exposure: number | null;
  drafts: number;
  totalDrafts: number;
}

interface PlatformSplitBarProps {
  segments: PlatformSplitSegment[];
  /** Track width; a number (px) or any CSS width. */
  width?: number | string;
}

/**
 * Stacked exposure bar for a mixed portfolio — one lane per platform, each
 * measured against its OWN draft count.
 *
 * Lanes are deliberately NOT summed into a single bar: 34% of Underdog and 12%
 * of DraftKings do not add up to 46% of anything, because the two denominators
 * are different. Two short lanes read as two rates; one long bar would read as
 * a total, which is exactly the misreading this replaces.
 */
export default function PlatformSplitBar({ segments, width = 56 }: PlatformSplitBarProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const c = surfaceTokens(isDark);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375, minWidth: 0 }}>
      {segments.map(seg => {
        const pct = seg.exposure ?? 0;
        const color = getPlatformColor(seg.provider, isDark);
        const label = getPlatformShortLabel(seg.provider);
        return (
          <Tooltip
            key={seg.provider}
            arrow
            title={
              seg.totalDrafts === 0
                ? `${seg.provider}: no drafts loaded`
                : `${seg.provider}: ${seg.drafts} of ${seg.totalDrafts} drafts (${pct.toFixed(1)}%)`
            }
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
              <Box
                component="span"
                sx={{
                  fontSize: 8.5, fontWeight: 700, letterSpacing: "0.04em",
                  color, width: 17, flexShrink: 0, lineHeight: 1,
                }}
              >
                {label}
              </Box>
              <Box
                sx={{
                  position: "relative", width, height: 6, flexShrink: 0,
                  bgcolor: c.surface2,
                  boxShadow: `inset 0 0 0 1px ${c.borderLight}`,
                  borderRadius: "3px", overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute", left: 0, top: 0, height: "100%",
                    width: `${Math.max(0, Math.min(pct, 100))}%`,
                    bgcolor: color, borderRadius: "3px",
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 11, fontVariantNumeric: "tabular-nums", lineHeight: 1,
                  color: seg.exposure === null ? c.dim : "text.primary",
                  minWidth: 34, textAlign: "right",
                }}
              >
                {seg.exposure === null ? "—" : `${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%`}
              </Box>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
